import {
  CreateUIAnalyticsEvent,
  useAnalyticsEvents
} from '@atlaskit/analytics-next';
import {
  GetAccountOrgsResponse,
  IStargateClient
} from '@atlassiansox/asc-stargate';
import { useStargateClient } from '@atlassiansox/bxpkit-core';
import { useEffect, useState } from 'react';
import {
  BITBUCKET_ANALYTICS_EVENTS,
  logBitbucketAnalytics
} from '../service/sign-up-form/bitbucket-analytics';

export type UseOrgInfoOptions = {
  readonly stargateClient: IStargateClient;
  readonly createAnalyticsEvent: CreateUIAnalyticsEvent;
} & OrgInfoRequest;

export type OrgInfoRequest = {
  readonly includeOrgResponse: boolean;
  readonly includeVortexMode: boolean;
};

export type OrgInfoResponse = {
  readonly orgList: GetAccountOrgsResponse | null;
  readonly hasError: boolean;
  readonly completed: boolean;
};

export const DEFAULT_ORG_RESPONSE = {
  orgList: [],
  hasError: false,
  completed: true
};

/**
 * Hook that provides org info relevant to signup
 */

export function useOrgInfo(request: OrgInfoRequest) {
  const stargateClient = useStargateClient();
  const { createAnalyticsEvent } = useAnalyticsEvents();

  return fetchOrgInfo({
    stargateClient,
    createAnalyticsEvent,
    ...request
  });
}

/**
 * Helper that invokes the org info API call
 */

export function fetchOrgInfo({
  includeOrgResponse,
  includeVortexMode,
  stargateClient,
  createAnalyticsEvent
}: UseOrgInfoOptions): OrgInfoResponse {
  if (!includeOrgResponse) {
    return { orgList: null, hasError: false, completed: true };
  }

  const [orgInfo, setOrgInfo] = useState<GetAccountOrgsResponse | null>(null);
  const [hasError, setHasError] = useState(false);
  const [completed, setCompleted] = useState(false);

  const fetchOrgInfo = async () => {
    await stargateClient
      .getAccountOrgs({ includeVortexMode: includeVortexMode })
      .then((orgs: GetAccountOrgsResponse) => {
        setOrgInfo(orgs);
      })
      .catch((error) => {
        setHasError(true);
        logBitbucketAnalytics({
          createAnalyticsEvent,
          analytics: {
            event: `${BITBUCKET_ANALYTICS_EVENTS.ORG_FETCH} fetched`,
            eventType: 'operational',
            action: 'errored',
            attributes: {
              errorCode: error?.response?.status,
              errorMessage: error?.message
            }
          }
        });
      });
  };

  useEffect(() => {
    fetchOrgInfo().then((_) => setCompleted(true));
  }, []);

  return { orgList: orgInfo, hasError, completed };
}
